/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Container,
  Box,
  Rating,
  Typography,
  Button,
  Stack,
  Alert,
  AlertTitle,
  Skeleton,
  TypographyProps,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { FIND_ZICASSO_MAPPING, RichTextDisplay } from '@marageti/z4-lib';
import { Destination } from '@marageti/z4-sdk/lib/travel';

export const mapFindZicassoToString = (findZicasso: string | undefined) => {
  if (findZicasso && findZicasso in FIND_ZICASSO_MAPPING) {
    return FIND_ZICASSO_MAPPING[findZicasso as keyof typeof FIND_ZICASSO_MAPPING];
  }
  return findZicasso;
};

const mapDestinationsListToString = (nextDestinations: string[], destinationsList?: Destination[]) => {
  if (!nextDestinations || !destinationsList) return '';

  const names = nextDestinations
    .map((id) => destinationsList.find((dest) => dest.id === id)?.name)
    .filter(Boolean);

  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names.join(', ');
};

const Section = ({ title = undefined, content, contentTypographyProps = undefined }:{
  title?: React.ReactNode | undefined,
  content: React.ReactNode,
  contentTypographyProps?: TypographyProps
}) => (
  <Stack spacing={1}>
    {title && (
      <Typography variant="bodyBold">
        {title}
      </Typography>
    )}
    <Box {...contentTypographyProps}>
      {content}
    </Box>
  </Stack>
);

const FinalPageView = ({
  subtitle,
  destinationsList,
  onSaveForLater,
  onSubmitReview,
  formValues,
  error,
}: {
  subtitle: React.ReactNode,
  destinationsList: any,
  onSaveForLater: (data: any) => any,
  onSubmitReview: (data: any) => any
  formValues: any,
  error: ApiError | null
}) => (
  <Container maxWidth="md" sx={{ my: 8.25 }}>
    <Stack spacing={3}>
      <Box>
        <Typography variant="body" color="primary">{subtitle}</Typography>
        <Typography variant="h2">Finalize Your Review</Typography>
      </Box>

      {/* Review Draft */}
      <Box>
        {formValues.rating ? (<Rating value={Number(formValues.rating)} readOnly size="small" />) : (<Skeleton width={100} />)}
        <Stack spacing={4}>
          {formValues.mainReview ? (
            <>
              <Section content={<RichTextDisplay>{formValues.mainReview}</RichTextDisplay>} />
              <Section
                title="What were the most enjorable or memorable parts of your trip?"
                content={<RichTextDisplay>{formValues.bestParts}</RichTextDisplay>}
              />
              {formValues.doDifferently && (
              <Section
                title="If you could take your trip again, what would you do differently?"
                content={<RichTextDisplay>{formValues.doDifferently}</RichTextDisplay>}
              />
              )}
              {formValues.nextDestinations && formValues.nextDestinations.length > 0 && (
                <Section
                  title="Where are you thinking of traveling next?"
                  content={<RichTextDisplay>{mapDestinationsListToString(formValues.nextDestinations, destinationsList)}</RichTextDisplay>}
                />
              )}
              {formValues.findZicasso && (
              <Section
                title="How did you learn about Zicasso?"
                content={mapFindZicassoToString(formValues.findZicasso)}
                contentTypographyProps={{
                  sx: {
                    textTransform: 'capitalize',
                  },
                }}
              />
              )}
            </>
          ) : (
            <>
              <Section
                title={
                  <Skeleton />
                }
                content={(
                  <>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                  </>
            )}
              />
              <Section
                title={
                  <Skeleton />
                }
                content={(
                  <>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                  </>
            )}
              />

            </>
          )}
          <Typography variant="captionItalic">
            We take the necessary measures to carefully modify reviews in order
            to safeguard the personal information of our esteemed partners and their associates.
          </Typography>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error">
          <AlertTitle>Submit Error</AlertTitle>
          {error.message}
          <br />
          Code:
          {' '}
          {error.code}
        </Alert>
      )}

      <Stack
        spacing={2.5}
        alignSelf={{
          mobile: 'initial',
          desktop: 'flex-start',
        }}
        direction={{
          mobile: 'column',
          desktop: 'row',
        }}
      >
        <Button
          variant="outlined"
          onClick={onSaveForLater}
          sx={{
            width: {
              desktop: 'fit-content',
            },
          }}
        >
          Save for Later
        </Button>
        <Button
          variant="contained"
          onClick={onSubmitReview}
          sx={{
            width: {
              desktop: 'fit-content',
            },
          }}
        >
          Submit Review
        </Button>
      </Stack>

    </Stack>
  </Container>
);

FinalPageView.Error = ({
  subtitle,
}: {
  subtitle: React.ReactNode
}) => (
  <Container maxWidth="md" sx={{ my: 8.25 }}>
    <Stack spacing={3}>
      <Box>
        <Typography variant="body" color="primary">{subtitle}</Typography>
        <Typography variant="h2">Finalize Your Review</Typography>
      </Box>
      <Alert severity="error">
        <AlertTitle>An error occured</AlertTitle>
        Please try again later.
      </Alert>
    </Stack>
  </Container>
);

export default FinalPageView;
